import { useState } from 'react';
import { X, ChevronLeft, ChevronRight, MapPin, Calendar, Users } from 'lucide-react';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  const galleryItems = [
    {
      id: 1,
      src: "/garbagetruck.jpg",
      title: "Medical Waste Collection",
      category: "Operations",
      location: "Mulago Hospital",
      date: "2024-01-15"
    },
    {
      id: 2,
      src: "/incenerator.jpeg",
      title: "Incineration Facility",
      category: "Facilities",
      location: "Ntinda Processing Center",
      date: "2024-01-08"
    },
    {
      id: 4,
      src: "/work.jpg",
      title: "Environmental Protection",
      category: "Sustainability",
      location: "Kampala Operations",
      date: "2024-01-05"
    },
    {
      id: 5,
      src: "/shredding.png",
      title: "Recycling Operations",
      category: "Recycling",
      location: "Material Recovery Center",
      date: "2024-01-03"
    },
    {
      id: 6,
      src: "/companyassets.jpg",
      title: "Transportation Fleet",
      category: "Logistics",
      location: "Fleet Operations",
      date: "2024-01-01"
    },
    {
      id: 7,
      src: "/shredding.png",
      title: "Safety Equipment",
      category: "Safety",
      location: "Equipment Center",
      date: "2023-12-28"
    },
    {
      id: 8,
      src: "/wastetreatment.jpeg",
      title: "Quality Control",
      category: "Operations",
      location: "Processing Facility",
      date: "2023-12-25"
    },
    {
      id: 9,
      src: "/teampicture.jpg",
      title: "Community Outreach",
      category: "Community",
      location: "Kampala Community Center",
      date: "2023-12-20"
    },
    {
      id: 10,
      src: "/biohaz training.jpg",
      title: "Safety Training Session",
      category: "Training",
      location: "BioHaz Training Center",
      date: "2024-02-10"
    },
    {
      id: 11,
      src: "/biohaz2.jpg",
      title: "Team Training Workshop",
      category: "Training",
      location: "BioHaz Training Center",
      date: "2024-02-15"
    }
  ];

  const categories = ["All", "Operations", "Training", "Facilities", "Sustainability", "Recycling", "Logistics", "Safety", "Community"];
  const [activeCategory, setActiveCategory] = useState("All");

  const filteredItems = activeCategory === "All" 
    ? galleryItems 
    : galleryItems.filter(item => item.category === activeCategory);

  const openModal = (index: number) => {
    setSelectedImage(index);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const goToPrevious = () => {
    setSelectedImage(prev => 
      prev !== null ? (prev > 0 ? prev - 1 : filteredItems.length - 1) : null
    );
  };

  const goToNext = () => {
    setSelectedImage(prev => 
      prev !== null ? (prev < filteredItems.length - 1 ? prev + 1 : 0) : null
    );
  };

  return (
    <div className="w-full overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 md:py-20 xl:py-24 bg-gradient-to-br from-[#295cab] to-[#1e4a8a] text-white w-full">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 md:px-8 xl:px-12 text-center w-full">
          <h1 className="text-2xl sm:text-4xl md:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6">Gallery</h1>
          <p className="text-base sm:text-lg md:text-xl xl:text-2xl max-w-3xl mx-auto leading-relaxed px-2 sm:px-4">
            Explore our operations, facilities, and team in action as we deliver professional waste management solutions
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-6 sm:py-8 bg-white sticky top-16 z-40 shadow-sm w-full">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-200 ${
                  activeCategory === category
                    ? 'bg-[#295cab] text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-blue-50 hover:text-[#295cab]'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-8 sm:py-12 md:py-16 xl:py-20 bg-gray-50 w-full">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {filteredItems.map((item, index) => (
              <div 
                key={item.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer"
                onClick={() => openModal(index)}
              >
                <div className="relative overflow-hidden">
                  <img 
                    src={item.src} 
                    alt={item.title}
                    className="w-full h-64 object-cover hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#295cab] text-white px-3 py-1 rounded-full text-sm font-medium">
                      {item.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <div className="space-y-1 sm:space-y-2">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm">{item.location}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm">{new Date(item.date).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      {selectedImage !== null && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={closeModal}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            
            <div className="bg-white rounded-lg overflow-hidden">
              <img 
                src={filteredItems[selectedImage].src} 
                alt={filteredItems[selectedImage].title}
                className="w-full max-h-96 object-cover"
              />
              <div className="p-6">
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-4">
                  {filteredItems[selectedImage].title}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4 text-gray-600">
                  <div className="flex items-center space-x-2">
                    <span className="bg-blue-50 text-[#1e4a8a] px-3 py-1 rounded-full text-sm font-medium">
                      {filteredItems[selectedImage].category}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4" />
                    <span>{filteredItems[selectedImage].location}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(filteredItems[selectedImage].date).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}

      {/* Statistics */}
      <section className="py-10 sm:py-14 md:py-20 xl:py-24 bg-white w-full">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold text-gray-900 mb-4">Our Impact in Images</h2>
            <p className="text-base sm:text-lg md:text-xl xl:text-2xl text-gray-600 max-w-2xl mx-auto">
              Visual documentation of our commitment to excellence and environmental responsibility
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            <div className="text-center p-8 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#295cab] text-white rounded-full mb-4">
                <Users className="w-8 h-8" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">500+</div>
              <div className="text-gray-600">Projects Documented</div>
            </div>
            
            <div className="text-center p-8 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 text-white rounded-full mb-4">
                <MapPin className="w-8 h-8" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">50+</div>
              <div className="text-gray-600">Locations Served</div>
            </div>
            
            <div className="text-center p-8 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1e4a8a] text-white rounded-full mb-4">
                <Calendar className="w-8 h-8" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">3+</div>
              <div className="text-gray-600">Years of Excellence</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;